/**
 * Verwaltung: eine einzelne Partie, unabhängig vom Eigentümer.
 *
 * Drei Bereiche: Partie-Metadaten (Name, Firma, Zeitsteuerung, Löschen),
 * Unternehmenswerte (Kasse, Schulden, Ruf, Kreditlinie) und die simulierten
 * Kunden dieser Partie. Jede Änderung läuft über das Verwaltungskommando
 * (`admin/editCompany` bzw. `admin/editCustomer`) und landet im Protokoll der
 * Partie – sichtbar für ihren Eigentümer.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CustomerSegments, type CustomerSegment } from '@hosting-sim/core';
import { Badge, Button, Card, type Column, DataTable, Input, Modal, Notice, Select, StatusDot } from '../components/ui';
import { api, type AdminCustomerDetail, type AdminCustomerEntry, type AdminGameDetail as AdminGameDetailData } from '../lib/api';
import { useTheme } from '../store/auth';
import { money, num } from '../lib/format';

const SEGMENT_LABEL: Record<CustomerSegment, string> = {
  private: 'Privat',
  smb: 'KMU',
  enterprise: 'Enterprise',
  reseller: 'Reseller',
};

export function AdminGameDetail() {
  const { gameId } = useParams<{ gameId: string }>();
  const { dark, toggle } = useTheme();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<AdminGameDetailData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!gameId) return;
    try {
      setDetail(await api.adminGameDetail(gameId));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [gameId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!gameId) return null;

  return (
    <div className="min-h-full">
      <header className="border-b divider">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/games')}>
            ← Partien
          </Button>
          <button
            type="button"
            onClick={toggle}
            aria-label="Farbschema wechseln"
            className="rounded-md px-2 py-1.5 text-sm text-panel-500 transition-colors hover:bg-panel-200/70 dark:text-panel-400 dark:hover:bg-panel-800"
          >
            {dark ? '☀' : '☾'}
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-5xl space-y-4 px-4 py-6 sm:px-6">
        {error && <Notice tone="danger">{error}</Notice>}
        {!detail && !error && <p className="text-sm text-panel-500 dark:text-panel-400">Wird geladen …</p>}

        {detail && (
          <>
            <GameCard gameId={gameId} detail={detail} onSaved={load} onDeleted={() => navigate('/admin/games')} />
            <CompanyCard gameId={gameId} detail={detail} onSaved={load} />
            <CustomersCard gameId={gameId} />
          </>
        )}
      </div>
    </div>
  );
}

function GameCard({
  gameId,
  detail,
  onSaved,
  onDeleted,
}: {
  gameId: string;
  detail: AdminGameDetailData;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const [name, setName] = useState(detail.game.name);
  const [companyName, setCompanyName] = useState(detail.game.companyName);
  const [running, setRunning] = useState(detail.game.running);
  const [speed, setSpeed] = useState(detail.game.speed);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.adminUpdateGame(gameId, { name, companyName, running, speed });
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.adminDeleteGame(gameId);
      onDeleted();
    } catch (e) {
      setError((e as Error).message);
      setDeleting(false);
    }
  };

  return (
    <Card
      title="Partie"
      subtitle={`Eigentümer: ${detail.owner.displayName} (${detail.owner.email})`}
      actions={
        <Button variant="danger" size="sm" onClick={() => setConfirmingDelete(true)}>
          Löschen
        </Button>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Partiename" value={name} onChange={(e) => setName(e.target.value)} />
        <Input label="Firma" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
        <div className="flex items-end gap-2">
          <Button variant={running ? 'primary' : 'secondary'} onClick={() => setRunning((v) => !v)}>
            {running ? 'Läuft' : 'Pausiert'}
          </Button>
          <div className="w-28">
            <Input
              label="Tempo"
              type="number"
              min={0}
              max={32}
              value={speed}
              onChange={(e) => setSpeed(Number(e.target.value))}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="mt-3">
          <Notice tone="danger">{error}</Notice>
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <Button variant="primary" loading={saving} onClick={() => void save()}>
          Speichern
        </Button>
      </div>

      <Modal
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        title="Partie löschen"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmingDelete(false)}>
              Abbrechen
            </Button>
            <Button variant="danger" loading={deleting} onClick={() => void remove()}>
              Endgültig löschen
            </Button>
          </>
        }
      >
        <p className="text-sm">
          Soll <strong>{detail.game.companyName}</strong> ({detail.game.name}) samt allen Spielständen endgültig gelöscht
          werden? Das lässt sich nicht rückgängig machen.
        </p>
      </Modal>
    </Card>
  );
}

function CompanyCard({ gameId, detail, onSaved }: { gameId: string; detail: AdminGameDetailData; onSaved: () => void }) {
  const c = detail.world.company;
  const [cash, setCash] = useState(c.cash);
  const [debt, setDebt] = useState(c.debt);
  const [reputation, setReputation] = useState(c.reputation);
  const [creditLimit, setCreditLimit] = useState(c.creditLimit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const patch: Record<string, number> = {};
      if (cash !== c.cash) patch.cash = cash;
      if (debt !== c.debt) patch.debt = debt;
      if (reputation !== c.reputation) patch.reputation = reputation;
      if (creditLimit !== c.creditLimit) patch.creditLimit = creditLimit;
      if (Object.keys(patch).length === 0) return;
      await api.adminEditCompany(gameId, patch);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title="Unternehmen"
      subtitle={`${num(detail.world.counts.customers)} Kunden · ${num(detail.world.counts.contracts)} aktive Verträge · ${num(detail.world.counts.servers)} Server`}
    >
      <div className="grid gap-3 sm:grid-cols-4">
        <Input label="Kasse (€)" type="number" value={cash} onChange={(e) => setCash(Number(e.target.value))} />
        <Input label="Schulden (€)" type="number" min={0} value={debt} onChange={(e) => setDebt(Number(e.target.value))} />
        <Input
          label="Ruf (0–100)"
          type="number"
          min={0}
          max={100}
          value={reputation}
          onChange={(e) => setReputation(Number(e.target.value))}
        />
        <Input
          label="Kreditlinie (€)"
          type="number"
          min={0}
          value={creditLimit}
          onChange={(e) => setCreditLimit(Number(e.target.value))}
        />
      </div>

      {error && (
        <div className="mt-3">
          <Notice tone="danger">{error}</Notice>
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <Button variant="primary" loading={saving} onClick={() => void save()}>
          Speichern
        </Button>
      </div>
    </Card>
  );
}

function CustomersCard({ gameId }: { gameId: string }) {
  const [customers, setCustomers] = useState<AdminCustomerEntry[] | null>(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminCustomerEntry | null>(null);

  const load = useCallback(
    async (query: string) => {
      try {
        const { customers } = await api.adminListCustomers(gameId, query || undefined);
        setCustomers(customers);
      } catch (e) {
        setError((e as Error).message);
      }
    },
    [gameId],
  );

  useEffect(() => {
    const handle = setTimeout(() => void load(search), 250);
    return () => clearTimeout(handle);
  }, [search, load]);

  const columns: Column<AdminCustomerEntry>[] = [
    {
      key: 'name',
      header: 'Kunde',
      sortValue: (c) => c.name,
      render: (c) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{c.name}</p>
          <p className="truncate text-2xs text-panel-500 dark:text-panel-400">{c.company ?? c.email}</p>
        </div>
      ),
    },
    {
      key: 'segment',
      header: 'Segment',
      hideOnMobile: true,
      render: (c) => <Badge tone="muted">{SEGMENT_LABEL[c.segment as CustomerSegment] ?? c.segment}</Badge>,
    },
    {
      key: 'satisfaction',
      header: 'Zufriedenheit',
      align: 'right',
      sortValue: (c) => c.satisfaction,
      render: (c) => <span className="tabular">{c.satisfaction.toFixed(0)}</span>,
    },
    {
      key: 'balance',
      header: 'Guthaben',
      align: 'right',
      sortValue: (c) => c.balance,
      render: (c) => <span className="tabular">{money(c.balance)}</span>,
    },
    {
      key: 'contracts',
      header: 'Verträge',
      align: 'right',
      hideOnMobile: true,
      sortValue: (c) => c.activeContracts,
      render: (c) => <span className="tabular">{c.activeContracts}</span>,
    },
    {
      key: 'tickets',
      header: 'Offene Tickets',
      align: 'right',
      hideOnMobile: true,
      sortValue: (c) => c.openTickets,
      render: (c) => (c.openTickets > 0 ? <StatusDot tone="warning" label={String(c.openTickets)} /> : <span className="tabular">0</span>),
    },
  ];

  return (
    <Card title="Kunden" subtitle="Simulierte Kunden dieser Partie – vollständig bearbeitbar.">
      <div className="mb-3">
        <Input placeholder="Name, Firma oder E-Mail suchen …" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {error && (
        <div className="mb-3">
          <Notice tone="danger">{error}</Notice>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={customers ?? []}
        rowKey={(c) => c.id}
        onRowClick={(c) => setEditing(c)}
        empty={customers === null ? 'Wird geladen …' : search ? 'Kein Kunde passt zur Suche.' : 'Keine Kunden vorhanden.'}
      />

      {editing && (
        <CustomerEditModal
          gameId={gameId}
          customerId={editing.id}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load(search);
          }}
        />
      )}
    </Card>
  );
}

function CustomerEditModal({
  gameId,
  customerId,
  onClose,
  onSaved,
}: {
  gameId: string;
  customerId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [detail, setDetail] = useState<AdminCustomerDetail | null>(null);
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [segment, setSegment] = useState<CustomerSegment>('private');
  const [satisfaction, setSatisfaction] = useState(0);
  const [balance, setBalance] = useState(0);
  const [churnRisk, setChurnRisk] = useState(0);
  const [reliability, setReliability] = useState(0);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const result = await api.adminGetCustomer(gameId, customerId);
        setDetail(result);
        setName(result.customer.name);
        setCompany(result.customer.company ?? '');
        setEmail(result.customer.email);
        setSegment(result.customer.segment as CustomerSegment);
        setSatisfaction(result.customer.satisfaction);
        setBalance(result.customer.balance);
        setChurnRisk(result.customer.churnRisk);
        setReliability(result.customer.reliability);
        setNotes(result.customer.notes);
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, [gameId, customerId]);

  const save = async () => {
    if (!detail) return;
    setSaving(true);
    setError(null);
    try {
      const c = detail.customer;
      const patch: Record<string, unknown> = {};
      if (name !== c.name) patch.name = name;
      if ((company || null) !== c.company) patch.company = company || null;
      if (email !== c.email) patch.email = email;
      if (segment !== c.segment) patch.segment = segment;
      if (satisfaction !== c.satisfaction) patch.satisfaction = satisfaction;
      if (balance !== c.balance) patch.balance = balance;
      if (churnRisk !== c.churnRisk) patch.churnRisk = churnRisk;
      if (reliability !== c.reliability) patch.reliability = reliability;
      if (notes !== c.notes) patch.notes = notes;
      if (Object.keys(patch).length > 0) await api.adminEditCustomer(gameId, customerId, patch);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      wide
      onClose={onClose}
      title="Kunde bearbeiten"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" loading={saving} disabled={!detail} onClick={() => void save()}>
            Speichern
          </Button>
        </>
      }
    >
      {!detail && !error && <p className="text-sm text-panel-500 dark:text-panel-400">Wird geladen …</p>}
      {error && <Notice tone="danger">{error}</Notice>}

      {detail && (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
            <Input label="Firma" value={company} onChange={(e) => setCompany(e.target.value)} />
            <Input label="E-Mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <Select label="Segment" value={segment} onChange={(e) => setSegment(e.target.value as CustomerSegment)}>
              {CustomerSegments.map((s) => (
                <option key={s} value={s}>
                  {SEGMENT_LABEL[s]}
                </option>
              ))}
            </Select>
            <Input
              label="Zufriedenheit (0–100)"
              type="number"
              min={0}
              max={100}
              value={satisfaction}
              onChange={(e) => setSatisfaction(Number(e.target.value))}
            />
            <Input label="Guthaben (€)" type="number" value={balance} onChange={(e) => setBalance(Number(e.target.value))} />
            <Input
              label="Kündigungsrisiko (0–1)"
              type="number"
              min={0}
              max={1}
              step={0.01}
              value={churnRisk}
              onChange={(e) => setChurnRisk(Number(e.target.value))}
            />
            <Input
              label="Zahlungsmoral (0–1)"
              type="number"
              min={0}
              max={1}
              step={0.01}
              value={reliability}
              onChange={(e) => setReliability(Number(e.target.value))}
            />
          </div>
          <label className="block">
            <span className="label mb-1 block">Notiz</span>
            <textarea
              className="w-full rounded-md border border-panel-300 bg-white px-2.5 py-1.5 text-sm placeholder:text-panel-400 focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500 dark:border-panel-700 dark:bg-panel-950"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>

          {detail.contracts.length > 0 && (
            <div>
              <p className="label mb-1">Verträge</p>
              <ul className="space-y-1 text-xs text-panel-600 dark:text-panel-300">
                {detail.contracts.map((c) => (
                  <li key={c.id}>
                    {c.productName} – {money(c.monthlyPrice)}/Monat ({c.state})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
